import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'

import Env from '../config/envConfig'
import { setUser } from '../../store/settings/settings.actions'
import { useDispatch } from 'react-redux'
import { addCsrfHeaders } from '../config/csrf'

const Login = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [login, setLogin] = useState<string>('')
  const [password, setPassword] = useState<string>('')

  const [forgotOpen, setForgotOpen] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)
  const [forgotError, setForgotError] = useState<string | null>(null)

  const _onLogin = () => {
    const loginRequest = {
      login,
      password,
    }
    const basePath = Env.basePath
    const restUrl = `${basePath}/api/session/login`

    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')
    addCsrfHeaders(requestHeaders)
    fetch(restUrl,
      {
        method: 'POST',
        body: JSON.stringify(loginRequest),
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(res => res.json())
      .then(() => {
        const userUrl = `${basePath}/api/session/user`
        fetch(userUrl,
          {
            method: 'GET',
            credentials: 'include',
            headers: requestHeaders,
          })
          .then(res => res.json())
          .then(json => {
            !json.error && dispatch(setUser(json))
          })
          .catch(err => console.log(err))
      })
      .catch(err => console.log(err))
  }

  const _onKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      _onLogin()
    }
  }

  const handleForgotOpen = () => {
    setForgotEmail('')
    setForgotSent(false)
    setForgotError(null)
    setForgotOpen(true)
  }

  const handleForgotSubmit = async () => {
    setForgotError(null)
    if (!forgotEmail.trim()) {
      setForgotError('Bitte gib deine E-Mail-Adresse ein.')
      return
    }
    setForgotLoading(true)
    try {
      const response = await fetch(`${Env.basePath}/api/user/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      })
      if (!response.ok) throw new Error('Anfrage fehlgeschlagen.')
      setForgotSent(true)
    } catch {
      setForgotError('Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.')
    } finally {
      setForgotLoading(false)
    }
  }

  return (
    <Container component='main' maxWidth='xs'>
      <CssBaseline />
      <div>
        <form noValidate>
          <TextField
            margin='normal'
            required
            fullWidth
            id='login'
            label={t('login:login')}
            name='login'
            autoComplete='login'
            autoFocus
            value={login}
            onChange={(event) => setLogin(event.currentTarget.value)}
            onKeyPress={_onKeyPress}
          />
          <TextField
            margin='normal'
            required
            fullWidth
            name='password'
            label={t('login:password')}
            type='password'
            id='password'
            autoComplete='current-password'
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
            onKeyPress={_onKeyPress}
          />
          <Button
            fullWidth
            variant='contained'
            color='primary'
            onClick={_onLogin}
          >
            {t('login:login')}
          </Button>
          <Button
            fullWidth
            variant='text'
            sx={{ mt: 1 }}
            onClick={handleForgotOpen}
          >
            Passwort vergessen?
          </Button>
        </form>
      </div>

      <Dialog open={forgotOpen} onClose={() => !forgotLoading && setForgotOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Passwort vergessen</DialogTitle>
        <DialogContent>
          {forgotSent ? (
            <Typography variant='body2'>
              Falls ein Account mit dieser E-Mail-Adresse existiert, erhältst du in Kürze eine E-Mail mit einem Link zum Zurücksetzen deines Passworts.
            </Typography>
          ) : (
            <>
              <Typography variant='body2' sx={{ mb: 2 }}>
                Gib deine E-Mail-Adresse ein. Wir senden dir einen Link zum Zurücksetzen deines Passworts.
              </Typography>
              <TextField
                fullWidth
                label='E-Mail-Adresse'
                type='email'
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={forgotLoading}
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleForgotSubmit()}
              />
              {forgotError && (
                <Typography variant='body2' color='error' sx={{ mt: 1 }}>
                  {forgotError}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotOpen(false)} disabled={forgotLoading}>
            {forgotSent ? 'Schließen' : 'Abbrechen'}
          </Button>
          {!forgotSent && (
            <Button
              onClick={handleForgotSubmit}
              variant='contained'
              disabled={forgotLoading || !forgotEmail.trim()}
              startIcon={forgotLoading ? <CircularProgress size={18} /> : undefined}
            >
              {forgotLoading ? 'Wird gesendet...' : 'Link senden'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  )
}
export default Login
